import { createTimestampedFileName, downloadTextFile, readFileAsText } from "../core/utils/file";
import { requireElement, setStatus } from "../core/utils/dom";
import type { Page } from "./Page";
import { ChartBuilderService } from "../features/charts/ChartBuilderService";
import { ChartModelParser } from "../features/charts/ChartModelParser";
import { ChartService } from "../features/charts/ChartService";
import { GeoJsonLayerService } from "../features/map/GeoJsonLayerService";
import { GeoJsonBounds } from "../features/geojson/GeoJsonBounds";
import { GeoJsonLoader } from "../features/geojson/GeoJsonLoader";
import { MapService } from "../features/map/MapService";

export class HomePage implements Page {
  private mapService = new MapService();
  private layerService: GeoJsonLayerService | null = null;
  private chartService = new ChartService();
  private chartBuilder: ChartBuilderService | null = null;

  render(): HTMLElement {
    const page = document.createElement("main");
    page.className = "page page--viewer";
    page.innerHTML = `
      <section class="hero card">
        <div>
          <p class="eyebrow">MapLibre + Terra Draw + OpenFreeMap</p>
          <h1>GIS map viewer</h1>
          <p>Import GeoJSON, inspect features, link popup charts by feature name, build chart JSON files, and print the current map.</p>
        </div>
      </section>

      <section class="toolbar card">
        <div class="field">
          <label for="select-map">Import Map GeoJSON</label>
          <input id="select-map" type="file" accept=".geojson,.json,application/geo+json,application/json" />
        </div>
        <div class="field">
          <label for="select-chart">Import Charts JSON</label>
          <input id="select-chart" type="file" accept=".json,application/json" />
        </div>
        <div class="field">
          <label for="select-chart-edit">Import Charts for Edit</label>
          <input id="select-chart-edit" type="file" accept=".json,application/json" />
        </div>
        <button id="load-sample-map" type="button">Load Sample Map</button>
        <button id="print-map" type="button">Print Map</button>
        <button id="close-all-charts" type="button">Close All Charts</button>
        <label class="checkbox"><input id="hide-zoom" type="checkbox" /> Hide zoom buttons</label>
      </section>

      <section class="map-shell card">
        <div id="viewer-map" class="map"></div>
        <div id="viewer-legend" class="legend-host"></div>
        <div id="chart-popup-host" class="chart-popup-host" aria-live="polite"></div>
      </section>

      <section class="card grid-2">
        <div>
          <h2>Status</h2>
          <div id="viewer-status" class="status status--info">Initializing map...</div>
          <h3>How to use</h3>
          <ol class="compact-list">
            <li>Load <code>sample.geojson</code> or import your own GeoJSON file.</li>
            <li>Import a chart JSON file, or generate one with the chart builder below.</li>
            <li>Make sure each feature chart <code>country_name</code> matches the GeoJSON feature <code>name</code>.</li>
            <li>Click a map feature to open its popup chart.</li>
          </ol>
        </div>
        <div id="chart-display-settings-host"></div>
        <div>
          <h2>Map and chart matching</h2>
          <p class="muted">The viewer links charts by feature name. A clicked feature with <code>properties.name = "Misr"</code> displays the chart whose <code>country_name</code> is <code>Misr</code>. GeoJSON property order does not matter.</p>
        </div>
      </section>

      <section class="card">
        <h2>Generate Chart</h2>
        <p class="muted">Define shared legends, then create one chart block for each map feature. Each feature chart has its own unit, caption, max value, and bar values so different countries or regions can use different scales.</p>
        <div class="help-panel">
          <h3>Chart builder parts</h3>
          <dl class="definition-list">
            <dt>Legend title</dt>
            <dd>Appears in the floating legend on the map. Use it as the group name for the bar colors, for example <code>Schools of law</code> or <code>Population groups</code>.</dd>
            <dt>Legends</dt>
            <dd>Defines the bar categories and colors shared by all popup charts. Each chart value name should match one legend name.</dd>
            <dt>Feature charts / country_name</dt>
            <dd>This is the map-link field. It must match the clicked GeoJSON feature <code>properties.name</code>. The name can appear anywhere inside the GeoJSON <code>properties</code> object; property order does not matter.</dd>
            <dt>Chart unit</dt>
            <dd>Belongs to each feature chart. It appears beside that feature's popup values, for example <code>%</code>, <code>km</code>, or <code>people</code>.</dd>
            <dt>Chart caption</dt>
            <dd>Belongs to each feature chart. It appears at the top of that feature's popup chart after the map feature is clicked.</dd>
            <dt>Chart max value</dt>
            <dd>Belongs to each feature chart and controls that chart's bar scale. Use this when different countries or regions need different maximum values.</dd>
            <dt>Feature chart values</dt>
            <dd>These are the bars inside the popup. The value name should match a legend name. The numeric value controls the bar width.</dd>
          </dl>
        </div>
        <div id="chart-builder"></div>
        <div class="button-row">
          <button id="generate-chart-json" type="button">Generate Chart JSON</button>
          <button id="apply-generated-chart-json" type="button">Apply Generated Chart</button>
          <button id="download-generated-chart-json" type="button">Download Generated Chart JSON</button>
        </div>
      </section>

      <section class="card">
        <h2>Chart JSON editor</h2>
        <p class="muted">Use this bottom editor to review, paste, apply, or download the chart JSON. Imported chart files and generated charts both appear here.</p>
        <textarea id="chart-json" class="code-area" spellcheck="false"></textarea>
        <div class="button-row">
          <button id="apply-chart-json" type="button">Apply Chart JSON</button>
          <button id="download-chart-json" type="button">Download Chart JSON</button>
        </div>
      </section>
    `;

    queueMicrotask(() => void this.initialize(page));
    return page;
  }

  destroy(): void {
    this.layerService?.clear();
    this.chartService.destroy();
    this.mapService.destroy();
  }

  private async initialize(page: HTMLElement): Promise<void> {
    const status = requireElement<HTMLDivElement>("#viewer-status", page);
    const chartJson = requireElement<HTMLTextAreaElement>("#chart-json", page);
    const legendHost = requireElement<HTMLDivElement>("#viewer-legend", page);
    const template = ChartModelParser.createTemplate();

    this.chartBuilder = new ChartBuilderService(requireElement<HTMLDivElement>("#chart-builder", page));
    this.chartBuilder.render(template);
    chartJson.value = ChartModelParser.stringify(template);
    this.chartService.setModel(template);
    this.chartService.drawLegend(legendHost);
    requireElement("#chart-display-settings-host", page).append(this.chartService.displaySettings.renderPanel());

    try {
      const map = this.mapService.initialize(requireElement("#viewer-map", page));
      await this.mapService.waitUntilReady();
      this.chartService.initialize(requireElement<HTMLDivElement>("#chart-popup-host", page), map);
      this.layerService = new GeoJsonLayerService(map);
      this.bindUi(page);
      setStatus(status, "Map is ready. Import a GeoJSON file or load the sample map.", "success");
    } catch (error) {
      setStatus(status, error instanceof Error ? error.message : "Failed to initialize the map.", "error");
    }
  }

  private bindUi(page: HTMLElement): void {
    const status = requireElement<HTMLDivElement>("#viewer-status", page);
    const chartJson = requireElement<HTMLTextAreaElement>("#chart-json", page);
    const legendHost = requireElement<HTMLDivElement>("#viewer-legend", page);

    requireElement<HTMLInputElement>("#select-map", page).addEventListener("change", async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file || !this.layerService) return;
      try {
        this.resetChartDisplaySettings(page);
        const document = await GeoJsonLoader.fromFile(file);
        const collection = await this.layerService.setData(document, ({ feature, lngLat }) => {
          this.chartService.openForProperties(feature.properties, lngLat);
        });
        setStatus(status, `Loaded ${file.name} with ${collection.features.length} feature(s). ${this.describeFeatureNames(collection)}${GeoJsonBounds.viewportNotice(document)}`, "success");
      } catch (error) {
        setStatus(status, error instanceof Error ? error.message : "Could not import GeoJSON.", "error");
      }
    });

    requireElement<HTMLInputElement>("#select-chart", page).addEventListener("change", async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        const model = ChartModelParser.parse(await readFileAsText(file));
        this.applyChartModel(model, chartJson, legendHost);
        setStatus(status, `Loaded chart file ${file.name}.`, "success");
      } catch (error) {
        setStatus(status, error instanceof Error ? error.message : "Could not import chart JSON.", "error");
      }
    });

    requireElement<HTMLInputElement>("#select-chart-edit", page).addEventListener("change", async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file || !this.chartBuilder) return;
      try {
        const model = ChartModelParser.parse(await readFileAsText(file));
        this.chartBuilder.setModel(model);
        this.applyChartModel(model, chartJson, legendHost);
        setStatus(status, `Loaded ${file.name} into the chart builder.`, "success");
      } catch (error) {
        setStatus(status, error instanceof Error ? error.message : "Could not import chart JSON for editing.", "error");
      }
    });

    requireElement<HTMLButtonElement>("#load-sample-map", page).addEventListener("click", async () => {
      if (!this.layerService) return;
      try {
        this.resetChartDisplaySettings(page);
        const document = await GeoJsonLoader.fromUrl("./geojson/sample.geojson");
        const collection = await this.layerService.setData(document, ({ feature, lngLat }) => {
          this.chartService.openForProperties(feature.properties, lngLat);
        });
        setStatus(status, `Loaded public/geojson/sample.geojson with ${collection.features.length} feature(s). ${this.describeFeatureNames(collection)}${GeoJsonBounds.viewportNotice(document)}`, "success");
      } catch (error) {
        setStatus(status, error instanceof Error ? error.message : "Could not load sample map.", "error");
      }
    });

    requireElement<HTMLButtonElement>("#apply-chart-json", page).addEventListener("click", () => {
      try {
        const model = ChartModelParser.parse(chartJson.value);
        this.applyChartModel(model, chartJson, legendHost);
        this.chartBuilder?.setModel(model);
        setStatus(status, "Chart JSON applied.", "success");
      } catch (error) {
        setStatus(status, error instanceof Error ? error.message : "Invalid chart JSON.", "error");
      }
    });

    requireElement<HTMLButtonElement>("#download-chart-json", page).addEventListener("click", () => {
      downloadTextFile(createTimestampedFileName("charts", "json"), chartJson.value);
    });

    requireElement<HTMLButtonElement>("#generate-chart-json", page).addEventListener("click", () => {
      try {
        const model = this.getGeneratedChartModel();
        this.applyChartModel(model, chartJson, legendHost);
        setStatus(status, "Generated chart JSON from the chart builder and applied it to the viewer.", "success");
      } catch (error) {
        setStatus(status, error instanceof Error ? error.message : "Could not generate chart JSON.", "error");
      }
    });

    requireElement<HTMLButtonElement>("#apply-generated-chart-json", page).addEventListener("click", () => {
      try {
        const model = this.getGeneratedChartModel();
        this.applyChartModel(model, chartJson, legendHost);
        setStatus(status, "Generated chart JSON applied to the viewer.", "success");
      } catch (error) {
        setStatus(status, error instanceof Error ? error.message : "Could not apply generated chart JSON.", "error");
      }
    });

    requireElement<HTMLButtonElement>("#download-generated-chart-json", page).addEventListener("click", () => {
      try {
        const model = this.getGeneratedChartModel();
        downloadTextFile(createTimestampedFileName("charts", "json"), ChartModelParser.stringify(model));
        setStatus(status, "Generated chart JSON downloaded.", "success");
      } catch (error) {
        setStatus(status, error instanceof Error ? error.message : "Could not download generated chart JSON.", "error");
      }
    });

    requireElement<HTMLInputElement>("#hide-zoom", page).addEventListener("change", (event) => {
      this.mapService.setZoomControls(!(event.target as HTMLInputElement).checked);
    });

    requireElement<HTMLButtonElement>("#print-map", page).addEventListener("click", () => window.print());
    requireElement<HTMLButtonElement>("#close-all-charts", page).addEventListener("click", () => this.chartService.closeAll());
    page.addEventListener("keydown", (event) => {
      if (event.key === "Escape") this.chartService.closeFocused();
    });
  }

  private describeFeatureNames(collection: { features: Array<{ properties?: Record<string, unknown> | null }> }): string {
    const names = collection.features
      .map((feature, index) => {
        const value = feature.properties?.name ?? feature.properties?.Name ?? feature.properties?.NAME;
        return typeof value === "string" && value.trim() ? value.trim() : `feature ${index + 1}: unnamed`;
      })
      .slice(0, 6);
    return names.length ? `Feature names: ${names.join(", ")}.` : "No named features found.";
  }

  private getGeneratedChartModel() {
    if (!this.chartBuilder) throw new Error("Chart builder is not ready.");
    return ChartModelParser.parse(ChartModelParser.stringify(this.chartBuilder.getModel()));
  }

  private resetChartDisplaySettings(page: HTMLElement): void {
    this.chartService.displaySettings.resetToDefaults();
    page.querySelectorAll<HTMLInputElement>(".chart-display-settings input[type=checkbox]").forEach((input) => {
      const name = input.getAttribute("aria-label");
      if (name) input.checked = true;
    });
  }

  private applyChartModel(model: ReturnType<typeof ChartModelParser.parse>, chartJson: HTMLTextAreaElement, legendHost: HTMLElement): void {
    this.chartService.setModel(model);
    chartJson.value = ChartModelParser.stringify(model);
    this.chartService.drawLegend(legendHost);
  }
}
